import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  Crown, Rocket, Target, ChevronRight, ChevronLeft, X, 
  Image, Users, Clock, Zap, Check, ArrowRight
} from 'lucide-react';

const SLIDES = [
  {
    id: 1,
    title: "Dein Creative steht. Jetzt beginnt das echte Business.",
    visual: "split",
    text: "Du hast bewiesen, wie schnell du professionelle Werbemittel erstellen kannst. Aber ein Bild allein bringt noch keine Kunden. Jetzt hast du zwei Wege zur Auswahl.",
    accent: "#00F2FF"
  },
  {
    id: 2,
    title: "Der manuelle Weg: Zeit gegen Geld.",
    visual: "business-manager",
    text: "Mit dem Starter-Paket (ab 19 €) schaltest du alle 22 Sparten frei. Du lädst deine Bilder herunter und baust die Kampagnen selbst im Facebook Business Manager.",
    subline: "Perfekt für Profis, die gerne 45 Minuten pro Anzeige im Meta-Backend verbringen.",
    accent: "#FF5C00"
  },
  {
    id: 3,
    title: "Der Agency-Killer Weg: In 10 Sekunden live.",
    visual: "launch-button",
    text: "Upgrade auf Flex (299 €) oder Elite (799 €) und überspringe das Meta-Chaos komplett. Verbinde dein Tool direkt mit Meta und schalte deine Ads per Klick live – ohne jemals den Business Manager öffnen zu müssen.",
    subline: "Werde zum Laie mit Profi-Ergebnissen. Keine Technik-Angst mehr.",
    accent: "#00F2FF"
  },
  {
    id: 4,
    title: "Hör auf zu raten – nutze Experten-Daten.",
    visual: "targeting",
    text: "Erreiche exakt die Menschen, die wirklich kaufen wollen. Schalte für einmalig 49 € den Targeting-Helfer frei. Kopiere die Zielgruppen-Einstellungen, die bei Top-Performern bereits Leads für unter 5 € generiert haben.",
    subline: "Einmal kaufen, dauerhaft präzise treffen.",
    accent: "#A78BFA"
  },
  {
    id: 5,
    title: "Wähle dein Level der Freiheit.",
    visual: "pricing-buttons",
    text: null,
    accent: "#FF5C00"
  }
];

// Visual Components for each slide
const SlideVisual = ({ type }) => {
  switch (type) {
    case 'split':
      return (
        <div className="flex gap-4 items-center justify-center py-6">
          <div className="w-24 h-24 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.1)', border: '2px solid rgba(0, 242, 255, 0.3)' }}>
            <Image className="w-10 h-10" style={{ color: '#00F2FF' }} />
          </div>
          <ArrowRight className="w-8 h-8" style={{ color: 'rgba(245, 245, 247, 0.5)' }} />
          <div className="w-24 h-24 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 92, 0, 0.1)', border: '2px solid rgba(255, 92, 0, 0.3)' }}>
            <Users className="w-10 h-10" style={{ color: '#FF5C00' }} />
          </div>
        </div>
      );
    
    case 'business-manager':
      return (
        <div className="py-6">
          <div className="relative rounded-xl overflow-hidden" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            {/* Fake Business Manager UI - Greyed out */}
            <div className="p-4 opacity-30">
              <div className="flex gap-2 mb-3">
                <div className="h-3 w-20 rounded" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}></div>
                <div className="h-3 w-16 rounded" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}></div>
                <div className="h-3 w-24 rounded" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}></div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="h-8 rounded" style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)' }}></div>
                <div className="h-8 rounded" style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)' }}></div>
                <div className="h-8 rounded" style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)' }}></div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-full rounded" style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)' }}></div>
                <div className="h-4 w-3/4 rounded" style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)' }}></div>
              </div>
            </div>
            {/* Overlay with time indicator */}
            <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(11, 14, 20, 0.8)' }}>
              <div className="text-center">
                <Clock className="w-12 h-12 mx-auto mb-2" style={{ color: '#FF5C00' }} />
                <span className="font-bold text-lg" style={{ color: '#FF5C00' }}>45+ Minuten</span>
                <p className="text-xs mt-1" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>pro Kampagne</p>
              </div>
            </div>
          </div>
        </div>
      );
    
    case 'launch-button':
      return (
        <div className="py-6 flex justify-center">
          <motion.div
            animate={{ 
              scale: [1, 1.05, 1],
              boxShadow: [
                '0 0 20px rgba(0, 242, 255, 0.3)',
                '0 0 40px rgba(0, 242, 255, 0.5)',
                '0 0 20px rgba(0, 242, 255, 0.3)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className="px-8 py-6 rounded-2xl cursor-pointer"
            style={{ 
              background: 'linear-gradient(135deg, #FF5C00 0%, #FF8A00 100%)',
            }}
          >
            <div className="flex items-center gap-3">
              <Rocket className="w-8 h-8 text-white" />
              <div className="text-left">
                <div className="text-white font-bold text-xl">LAUNCH</div>
                <div className="text-white/70 text-xs">Mit einem Klick live</div>
              </div>
            </div>
          </motion.div>
        </div>
      );
    
    case 'targeting':
      return (
        <div className="py-6 flex justify-center">
          <div className="relative">
            <div className="w-28 h-28 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '2px solid #A78BFA' }}>
              <Target className="w-14 h-14" style={{ color: '#A78BFA' }} />
            </div>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3 }}
              className="absolute -top-2 -right-2 w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#00F2FF' }}
            >
              <Check className="w-6 h-6" style={{ color: '#0B0E14' }} />
            </motion.div>
          </div>
        </div>
      );
    
    case 'pricing-buttons':
      return null;
    
    default:
      return null;
  }
};

const OnboardingSlideshow = ({ isOpen, onClose, onSelectPlan }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  if (!isOpen) return null;

  const slide = SLIDES[currentSlide];
  const isLastSlide = currentSlide === SLIDES.length - 1;
  const isFirstSlide = currentSlide === 0;

  const nextSlide = () => {
    if (!isLastSlide) {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const prevSlide = () => {
    if (!isFirstSlide) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(11, 14, 20, 0.95)' }}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="max-w-xl w-full rounded-2xl p-6 relative overflow-hidden"
          style={{ 
            backgroundColor: '#0B0E14',
            border: `1px solid ${slide.accent}40`
          }}
        >
          {/* Accent Bar */}
          <div 
            className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" 
            style={{ backgroundColor: slide.accent }} 
          />
          
          {/* Progress Dots */}
          <div className="flex justify-center gap-2 mb-6">
            {SLIDES.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className="h-2 rounded-full transition-all"
                style={{ 
                  backgroundColor: index === currentSlide ? slide.accent : 'rgba(255, 255, 255, 0.2)',
                  width: index === currentSlide ? '24px' : '8px'
                }}
              />
            ))}
          </div>

          {/* Slide Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Title */}
              <h2 
                className="text-xl md:text-2xl font-bold text-center mb-4"
                style={{ color: '#F5F5F7' }}
              >
                {slide.title}
              </h2>

              {/* Visual */}
              <SlideVisual type={slide.visual} />

              {/* Text */}
              {slide.text && (
                <p className="text-center text-sm md:text-base leading-relaxed mb-3" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                  {slide.text}
                </p>
              )}

              {/* Subline */}
              {slide.subline && (
                <p className="text-center text-xs italic mb-4" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>
                  {slide.subline}
                </p>
              )}

              {/* Final Slide: Pricing Buttons */}
              {isLastSlide && (
                <div className="space-y-3 mt-6">
                  {/* Starter Option */}
                  <Button
                    onClick={() => onSelectPlan('starter')}
                    className="w-full py-5 text-base font-bold rounded-xl transition-all hover:scale-[1.02]"
                    style={{ 
                      backgroundColor: 'rgba(0, 242, 255, 0.1)',
                      border: '1px solid rgba(0, 242, 255, 0.3)',
                      color: '#00F2FF'
                    }}
                  >
                    <Crown className="w-5 h-5 mr-2" />
                    Starter: Alle 22 Vorlagen nutzen (ab 19 €)
                  </Button>

                  {/* Elite Option - FOCUS */}
                  <Button
                    onClick={() => onSelectPlan('elite')}
                    className="w-full py-6 text-lg font-bold rounded-xl transition-all hover:scale-[1.02]"
                    style={{ 
                      background: 'linear-gradient(135deg, #FF5C00 0%, #FF8A00 100%)',
                      color: 'white',
                      boxShadow: '0 0 30px rgba(255, 92, 0, 0.4)'
                    }}
                  >
                    <Rocket className="w-6 h-6 mr-2" />
                    Elite: Nie wieder Business Manager (799 €)
                  </Button>
                  <p className="text-center text-xs" style={{ color: '#00F2FF' }}>
                    ⚡ Direkt-Sync freischalten – Mit einem Klick live auf Meta
                  </p>

                  {/* Targeting Helper Option */}
                  <Button
                    onClick={() => onSelectPlan('targeting')}
                    variant="outline"
                    className="w-full py-4 text-sm font-semibold rounded-xl"
                    style={{ 
                      backgroundColor: 'rgba(167, 139, 250, 0.1)',
                      border: '1px solid rgba(167, 139, 250, 0.3)',
                      color: '#A78BFA'
                    }}
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Targeting-Helfer: Experten-Listen sichern (49 €)
                  </Button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          {!isLastSlide && (
            <div className="flex justify-between items-center mt-6">
              <Button
                onClick={prevSlide}
                disabled={isFirstSlide}
                variant="ghost"
                className={`${isFirstSlide ? 'opacity-30' : 'opacity-100'}`}
                style={{ color: 'rgba(245, 245, 247, 0.7)' }}
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Zurück
              </Button>
              
              <Button
                onClick={nextSlide}
                className="font-semibold"
                style={{ 
                  background: `linear-gradient(135deg, ${slide.accent}cc 0%, ${slide.accent} 100%)`,
                  color: '#0B0E14'
                }}
              >
                Weiter
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
          )}

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 transition-colors"
            style={{ color: 'rgba(245, 245, 247, 0.5)' }}
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* "Später" Option on last slide */}
          {isLastSlide && (
            <button
              onClick={onClose}
              className="w-full mt-4 text-center text-sm transition-colors"
              style={{ color: 'rgba(245, 245, 247, 0.5)' }}
            >
              Später entscheiden
            </button>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingSlideshow;
